import { useState, useEffect } from "react";
import {
  Modal,
  Stack,
  Text,
  Checkbox,
  Button,
  Group,
  ScrollArea,
  Loader,
  Center,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  usePracticesForConfirmation,
  useCreateControlConfirmation,
} from "../../hooks/useWorkflows";

interface ControlConfirmationModalProps {
  opened: boolean;
  onClose: () => void;
}

export function ControlConfirmationModal({
  opened,
  onClose,
}: ControlConfirmationModalProps) {
  const { data: practices, isLoading } = usePracticesForConfirmation({
    enabled: opened,
  });
  const confirmMutation = useCreateControlConfirmation();

  const [checkedPractices, setCheckedPractices] = useState<Set<string>>(
    new Set()
  );

  // Reset checked practices when modal opens
  useEffect(() => {
    if (opened) {
      setCheckedPractices(new Set());
    }
  }, [opened]);

  // Auto-select when there is only one practice option
  useEffect(() => {
    if (opened && practices && practices.length === 1) {
      setCheckedPractices(new Set([practices[0].id]));
    }
  }, [opened, practices]);

  const handleCheckChange = (practiceId: string, checked: boolean) => {
    setCheckedPractices((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(practiceId);
      } else {
        next.delete(practiceId);
      }
      return next;
    });
  };

  const allChecked = practices
    ? practices.every((p) => checkedPractices.has(p.id))
    : false;

  const handleConfirm = async () => {
    if (!practices) return;

    try {
      await confirmMutation.mutateAsync(Array.from(checkedPractices));
      notifications.show({
        title: "Controls Confirmed",
        message: "H&E control slides have been confirmed for today.",
        color: "green",
      });
      onClose();
    } catch (error: any) {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to confirm controls",
        color: "red",
      });
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="H&E CONTROL CONFIRMATION"
      size="md"
      centered
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Confirm H&E control slides have been completed for:
        </Text>

        {isLoading ? (
          <Center py="xl">
            <Loader size="sm" />
          </Center>
        ) : (
          <ScrollArea.Autosize mah={300}>
            <Stack gap="xs">
              {practices?.map((practice) => (
                <Checkbox
                  key={practice.id}
                  label={practice.name}
                  checked={checkedPractices.has(practice.id)}
                  onChange={(e) =>
                    handleCheckChange(practice.id, e.currentTarget.checked)
                  }
                />
              ))}
            </Stack>
          </ScrollArea.Autosize>
        )}

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!allChecked}
            loading={confirmMutation.isPending}
          >
            Confirm
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
